import { produce } from "immer";
import * as uiActions from "store/actions/uiActions";
import storage from "@storage";

const MAX_HANDLED_NEW_MESSAGES = 1000;

const normalizeId = id =>
  id === undefined || id === null || id === "" || String(id) === "0"
    ? null
    : String(id);

const createPendingMessage = (message, sequence) => {
  const messageId = normalizeId(message?.message_id ?? message?.id);
  const conversationId = normalizeId(message?.conversation_id);
  const firstMessageId = normalizeId(message?.first_message_id);

  return {
    key: messageId ? `message:${messageId}` : `anonymous:${sequence}`,
    messageId,
    conversationId,
    firstMessageId
  };
};

const matchesHandledMessage = (pending, message) => {
  if (!message) return false;

  const messageId = normalizeId(message.message_id ?? message.id);
  if (!messageId) return false;

  if (message.listingType !== "conversation") {
    return pending.messageId === messageId;
  }

  const conversationId = normalizeId(message.conversation_id) || messageId;
  return (
    pending.messageId === messageId ||
    pending.firstMessageId === messageId ||
    pending.conversationId === conversationId
  );
};

const ensureNewMessageState = draft => {
  if (!Array.isArray(draft.pendingNewMessageItems)) {
    draft.pendingNewMessageItems = [];
  }
  if (!Array.isArray(draft.handledNewMessageKeys)) {
    draft.handledNewMessageKeys = [];
  }
  if (!Number.isInteger(draft.newMessageSequence)) {
    draft.newMessageSequence = 0;
  }
};

const rememberHandled = (draft, keys) => {
  if (keys.length === 0) return;

  draft.handledNewMessageKeys = [
    ...new Set([...draft.handledNewMessageKeys, ...keys])
  ].slice(-MAX_HANDLED_NEW_MESSAGES);
};

const syncPendingCount = draft => {
  draft.pendingNewMessages = draft.pendingNewMessageItems.length;
};

const initialState = {
  folder: "inbox",
  page: 1,
  query: null,
  messageListingType: storage.get("message_listing_type") || "conversation",
  unread: false,
  sort: {
    name: "date",
    type: "desc"
  },
  pendingNewMessages: 0,
  pendingNewMessageItems: [],
  handledNewMessageKeys: [],
  newMessageSequence: 0
};

const reducer = produce((draft, action) => {
  switch (action.type) {
    case uiActions.SET_UNREAD_STATUS: {
      draft.unread = action.payload;
      break;
    }

    case uiActions.CHANGE_FOLDER: {
      let { folder } = action.payload;

      draft.folder = folder;
      break;
    }

    case uiActions.INBOX_NEW_MESSAGES_RECEIVED: {
      ensureNewMessageState(draft);
      const pending = createPendingMessage(
        action.payload,
        draft.newMessageSequence
      );

      const wasAlreadyHandled = draft.handledNewMessageKeys.includes(
        pending.key
      );
      const isAlreadyPending = draft.pendingNewMessageItems.some(
        item => item.key === pending.key
      );

      if (!wasAlreadyHandled && !isAlreadyPending) {
        draft.pendingNewMessageItems.push(pending);
        draft.newMessageSequence += 1;
        syncPendingCount(draft);
      }
      break;
    }

    case uiActions.INBOX_NEW_MESSAGES_CLEAR: {
      ensureNewMessageState(draft);
      rememberHandled(
        draft,
        draft.pendingNewMessageItems.map(item => item.key)
      );
      draft.pendingNewMessageItems = [];
      syncPendingCount(draft);
      break;
    }

    case uiActions.INBOX_NEW_MESSAGES_HANDLED: {
      ensureNewMessageState(draft);
      const messages = action.payload?.messages || [];
      const removed = draft.pendingNewMessageItems.filter(pending =>
        messages.some(message => matchesHandledMessage(pending, message))
      );

      if (removed.length > 0) {
        const removedKeys = new Set(removed.map(item => item.key));
        draft.pendingNewMessageItems = draft.pendingNewMessageItems.filter(
          item => !removedKeys.has(item.key)
        );
        rememberHandled(
          draft,
          removed.map(item => item.key)
        );
        syncPendingCount(draft);
      }
      break;
    }

    case uiActions.CHANGE_PAGE: {
      let { page, query } = action.payload;

      draft.page = page;
      if (query !== undefined) {
        draft.query = query;
      }
      break;
    }

    case uiActions.CHANGE_QUERY: {
      draft.query = action.payload;
      break;
    }

    case uiActions.SET_LISTING_TYPE: {
      let { text } = action.payload;

      draft.messageListingType = text;
      break;
    }

    case uiActions.SET_SORT: {
      draft.sort = action.payload.sort;
      break;
    }

    default:
      return draft;
  }
}, initialState);

export default reducer;
